/**
 * File validation utilities with security checks
 */

// File signatures (magic bytes) for common image formats
const FILE_SIGNATURES: Record<string, number[]> = {
  'image/png': [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A],
  'image/jpeg': [0xFF, 0xD8, 0xFF],
  'image/jpg': [0xFF, 0xD8, 0xFF],
  'image/webp': [0x52, 0x49, 0x46, 0x46],
};

/**
 * Validates an image file with multiple security checks
 * @param file The file to validate
 * @returns Object with validation result and optional error message
 */
export async function validateImageFile(
  file: File
): Promise<{ valid: boolean; error?: string }> {
  // 1. Check file extension
  const validExtensions = ['.png', '.jpg', '.jpeg', '.webp'];
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

  if (!validExtensions.includes(extension)) {
    return {
      valid: false,
      error: 'Only PNG, JPEG, and WebP images are allowed. SVG files are not supported for security reasons.',
    };
  }

  // 2. Check MIME type
  if (!file.type.startsWith('image/')) {
    return {
      valid: false,
      error: 'File must be an image. Please upload a valid image file.',
    };
  }

  // 3. Check file size (2MB max)
  const maxSize = 2 * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File too large. Maximum size is 2MB.',
    };
  }

  // 4. Validate magic bytes (file signature)
  try {
    const arrayBuffer = await file.slice(0, 12).arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    const signature = FILE_SIGNATURES[file.type];

    if (signature) {
      const isValidSignature = signature.every((byte, index) => bytes[index] === byte);

      if (!isValidSignature) {
        return {
          valid: false,
          error: `Invalid image file. The file content doesn't match a valid ${file.type.split('/')[1].toUpperCase()} format.`,
        };
      }
    }
  } catch (error) {
    return {
      valid: false,
      error: 'Failed to read file. Please try again.',
    };
  }

  // 5. Try to load the image to ensure it's valid
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    // Set a timeout to prevent hanging
    const timeout = setTimeout(() => {
      URL.revokeObjectURL(url);
      resolve({
        valid: false,
        error: 'Image loading timeout. The file may be corrupted.',
      });
    }, 10000); // 10 second timeout

    img.onload = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(url);

      // Check image dimensions (max 2048x2048)
      if (img.width > 2048 || img.height > 2048) {
        resolve({
          valid: false,
          error: `Image too large. Maximum dimensions are 2048x2048 pixels. Your image is ${img.width}x${img.height}.`,
        });
        return;
      }

      // Check minimum dimensions (at least 10x10)
      if (img.width < 10 || img.height < 10) {
        resolve({
          valid: false,
          error: `Image too small. Minimum dimensions are 10x10 pixels. Your image is ${img.width}x${img.height}.`,
        });
        return;
      }

      resolve({ valid: true });
    };

    img.onerror = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      resolve({
        valid: false,
        error: 'Failed to load image. The file may be corrupted or not a valid image.',
      });
    };

    img.src = url;
  });
}

/**
 * Checks if a file is a valid image without loading it
 * @param file The file to check
 * @returns true if file appears to be a valid image
 */
export function isValidImageFile(file: File): boolean {
  const validExtensions = ['.png', '.jpg', '.jpeg', '.webp'];
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  return validExtensions.includes(extension) && file.type.startsWith('image/');
}
